import { Flex, Tooltip } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';

import { DateService } from '@shared/services/date';
import { Text } from '@shared/ui/antd';
import { formatMessage } from '@modules/dialogs/ui/chat-window/ui/virtualized-messages-list/ui/message-item/utils';
import messageItemStyles from '@modules/dialogs/ui/chat-window/ui/virtualized-messages-list/ui/message-item/message-item.module.scss';

import type { UiMessage } from '../lib/types';
import { splitMessageSeries } from '../lib/utils';
import styles from '../agent-test-chat.module.scss';

interface AgentTestMessageItemProps {
  message: UiMessage;
  switchNoticeText?: string | null;
  switchNoticeTooltip?: string;
}

export const AgentTestMessageItem: FC<AgentTestMessageItemProps> = ({
  message,
  switchNoticeText,
  switchNoticeTooltip,
}) => {
  const isUser = message.creator_type === 'USER';
  const timeLabel = message.created_at ? DateService.format(message.created_at, 'HH:mm') : '-';
  const messageParts = splitMessageSeries(message.message);

  if (message.isTypingIndicator) {
    return (
      <Flex className={messageItemStyles.root} align="flex-end">
        <div className={messageItemStyles.message__wrapper}>
          <div
            className={classNames(
              messageItemStyles.message__container,
              messageItemStyles.message__container_client,
            )}
          >
            <div className={styles.typingIndicator} aria-label="Typing indicator">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      </Flex>
    );
  }
  if (isUser) {
    return (
      <>
        <div className={styles.messageSeries}>
          {messageParts.map((part) => (
            <Flex
              key={`${message.id}-${part.key}`}
              className={classNames(
                messageItemStyles.root,
                messageItemStyles.root_admin,
                styles.messageSeriesItem,
              )}
              justify="flex-end"
              align="flex-end"
            >
              <div
                className={classNames(messageItemStyles.message__wrapper, styles.userMessageWrapper)}
              >
                <div
                  className={classNames(
                    messageItemStyles.message__container,
                    messageItemStyles.message__container_admin,
                    styles.userMessageContainer,
                  )}
                >
                  <p
                    className={messageItemStyles.text}
                    dangerouslySetInnerHTML={{ __html: formatMessage(part.text) }}
                  />

                  <div className={classNames(styles.messageTimeRow, styles.messageTimeRowClient)}>
                    <Text variant="xs" fontWeight={400} className={messageItemStyles.text__time}>
                      {timeLabel}
                    </Text>
                  </div>
                </div>
              </div>
            </Flex>
          ))}
        </div>

        {switchNoticeText && (
          <div className={styles.switchNoticeRow}>
            <Tooltip title={switchNoticeTooltip} placement="top">
              <Text variant="xs" className={styles.switchNoticeText}>
                {switchNoticeText}
              </Text>
            </Tooltip>
          </div>
        )}
      </>
    );
  }

  return (
    <div className={styles.messageSeries}>
      {messageParts.map((part) => (
        <Flex
          key={`${message.id}-${part.key}`}
          className={classNames(messageItemStyles.root, styles.userMessageRoot, styles.messageSeriesItem)}
          align="flex-end"
          gap={12}
        >
          <div className={messageItemStyles.message__wrapper}>
            <div
              className={classNames(
                messageItemStyles.message__container,
                messageItemStyles.message__container_client,
              )}
            >
              <p
                className={messageItemStyles.text}
                dangerouslySetInnerHTML={{ __html: formatMessage(part.text) }}
              />

              <div className={classNames(styles.messageTimeRow, styles.messageTimeRowAgent)}>
                <Text variant="xs" fontWeight={400} className={messageItemStyles.text__time}>
                  {timeLabel}
                </Text>
              </div>
            </div>
          </div>
        </Flex>
      ))}
    </div>
  );
};
